import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { endOfDay, endOfMonth, formatISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronUp, Download, RefreshCw, UserRound } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canViewCustomerAnalytics } from "../../utils/roles";
import { formatDateTime } from "../../utils/analyticsDates";
import {
  exportCustomerCheckouts,
  fetchCheckoutCouponCodes,
  fetchCustomerCheckout,
  fetchCustomerCheckouts,
} from "../../api/CustomerAnalyticsAPIs";

const DATE_OPTIONS = [
  { id: "today", label: "اليوم" },
  { id: "yesterday", label: "أمس" },
  { id: "7", label: "آخر 7 أيام" },
  { id: "30", label: "آخر 30 يوم" },
  { id: "month", label: "هذا الشهر" },
  { id: "custom", label: "نطاق مخصص" },
];

const STATUS_OPTIONS = [
  { id: "all", label: "الكل" },
  { id: "noOrder", label: "كتب بياناته ولم يأكد الطلب" },
  { id: "unpaid", label: "أكد الطلب والدفع الأونلاين ماتمش" },
  { id: "placed", label: "أكد الطلب" },
];

const CONTACT_OPTIONS = [
  { id: "reachable", label: "عنده موبايل أو إيميل" },
  { id: "phone", label: "عنده موبايل" },
  { id: "email", label: "عنده إيميل" },
  { id: "both", label: "عنده الاتنين" },
  { id: "all", label: "الكل حتى بدون بيانات" },
];

const ORDER_STATUS_OPTIONS = [
  { id: "all", label: "كل حالات الطلب" },
  { id: "New", label: "جديد" },
  { id: "Old", label: "قديم" },
  { id: "Confirmed", label: "متأكد" },
  { id: "Canceled", label: "ملغي" },
  { id: "Cancelled by Customer", label: "العميل لغاه" },
  { id: "Hold", label: "موقوف" },
  { id: "Deposit Required", label: "محتاج عربون" },
  { id: "Incomplete", label: "ناقص" },
  { id: "Custom", label: "طلب خاص" },
  { id: "Under Purchase", label: "تحت الشراء" },
  { id: "Waiting Confirming", label: "مستني تأكيد" },
  { id: "Confirming", label: "جاري التأكيد" },
  { id: "Printed", label: "اتطبع" },
  { id: "Printlet", label: "برنتلت" },
  { id: "TeePlus", label: "تي بلس" },
  { id: "Girabatcom", label: "جرابتكم" },
  { id: "Spam", label: "سبام" },
];

const ORDER_STATUS_LABELS = Object.fromEntries(ORDER_STATUS_OPTIONS.filter((option) => option.id !== "all").map((option) => [option.id, option.label]));

const OUTCOME_SHORT = {
  NoOrder: "مأكدش الطلب",
  Unpaid: "أكد والدفع ناقص",
  Placed: "أكد الطلب",
};

const rangeFor = (preset, customFrom, customTo) => {
  const now = new Date();
  let from;
  let to;
  if (preset === "today") {
    from = startOfDay(now);
    to = endOfDay(now);
  } else if (preset === "yesterday") {
    const day = subDays(now, 1);
    from = startOfDay(day);
    to = endOfDay(day);
  } else if (preset === "7") {
    from = startOfDay(subDays(now, 6));
    to = endOfDay(now);
  } else if (preset === "month") {
    from = startOfMonth(now);
    to = endOfMonth(now);
  } else if (preset === "custom") {
    from = customFrom ? startOfDay(new Date(customFrom)) : startOfDay(subDays(now, 29));
    to = customTo ? endOfDay(new Date(customTo)) : endOfDay(now);
  } else {
    from = startOfDay(subDays(now, 29));
    to = endOfDay(now);
  }
  return { from: formatISO(from), to: formatISO(to) };
};

const paymentLabel = (method) => {
  if (method === "Online") return "أونلاين";
  if (method === "COD") return "عند الاستلام";
  return "—";
};

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return `${amount.toLocaleString("ar-EG")} ج.م`;
};

const outcomeOf = (item) => {
  if (item?.outcome) return item.outcome;
  if (item?.orderId) return "Placed";
  if (item?.pendingOrderId) return "Unpaid";
  return "NoOrder";
};

const outcomeTextOf = (item) => {
  if (item?.outcomeText) return item.outcomeText;
  if (item?.orderId) return `أكد الطلب رقم #${item.orderId}`;
  if (item?.pendingOrderId) return `أكد الطلب رقم #${item.pendingOrderId} لكن الدفع الأونلاين لسه ماتمش`;
  return "كتب بياناته في صفحة الدفع ولم يأكد الطلب";
};

const paymentFinishedLabel = (item) => {
  if (item?.paymentFinishedText) return item.paymentFinishedText;
  if (!item?.orderId && !item?.pendingOrderId) return "لا";
  if (!item?.orderId) return "لا";
  if (item.paymentMethod === "COD") return "عند الاستلام";
  return item.paymentFinished ? "نعم" : "لا";
};

const orderStatusLabel = (item) => {
  if (item?.orderStatusText) return item.orderStatusText;
  if (item?.orderStatus && ORDER_STATUS_LABELS[item.orderStatus]) return ORDER_STATUS_LABELS[item.orderStatus];
  if (item?.orderId || item?.pendingOrderId) return item.orderStatus || "—";
  return "مفيش طلب";
};

const statusClass = (outcome) => {
  if (outcome === "Placed") return "bg-emerald-100 text-emerald-800";
  if (outcome === "Unpaid") return "bg-amber-100 text-amber-800";
  if (outcome === "NoOrder") return "bg-rose-100 text-rose-800";
  return "bg-sky-100 text-sky-800";
};

const Field = ({ label, children }) => (
  <label className="block text-sm text-slate-600">
    <span className="mb-1 block font-medium">{label}</span>
    {children}
  </label>
);

const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400";

export default function CustomerAnalyticsPage() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [preset, setPreset] = useState("30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [status, setStatus] = useState("all");
  const [coupon, setCoupon] = useState("all");
  const [couponCode, setCouponCode] = useState("");
  const [payment, setPayment] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [hasWishlist, setHasWishlist] = useState(false);
  const [hasCart, setHasCart] = useState(false);
  const [reachedCheckout] = useState(true);
  const [placedOrder] = useState(false);
  const [abandonMinutes] = useState(30);
  const [contact, setContact] = useState("reachable");
  const [orderStatus, setOrderStatus] = useState("all");
  const [minAmount, setMinAmount] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState("checkoutDate");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (!authState.token) {
      navigate("/login");
      return;
    }
    if (!canViewCustomerAnalytics(authState.role)) navigate("/dashboard");
  }, [authState, navigate]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [preset, customFrom, customTo, status, coupon, couponCode, payment, search, hasWishlist, hasCart, reachedCheckout, placedOrder, abandonMinutes, contact, orderStatus, minAmount, pageSize, sort, sortDir]);

  const filters = useMemo(() => {
    const range = rangeFor(preset, customFrom, customTo);
    return {
      ...range,
      status,
      coupon,
      couponCode: couponCode.trim(),
      payment,
      search,
      hasWishlist,
      hasCart,
      reachedCheckout,
      placedOrder,
      abandonMinutes,
      contact,
      orderStatus,
      minAmount: minAmount.trim(),
      page,
      pageSize,
      sort,
      sortDir,
    };
  }, [preset, customFrom, customTo, status, coupon, couponCode, payment, search, hasWishlist, hasCart, reachedCheckout, placedOrder, abandonMinutes, contact, orderStatus, minAmount, page, pageSize, sort, sortDir]);

  const listQuery = useQuery({
    queryKey: ["customer-checkouts", filters],
    queryFn: () => fetchCustomerCheckouts(filters),
    placeholderData: keepPreviousData,
    enabled: Boolean(authState.token) && canViewCustomerAnalytics(authState.role),
  });

  const codesQuery = useQuery({
    queryKey: ["customer-checkout-coupons"],
    queryFn: () => fetchCheckoutCouponCodes(""),
    enabled: Boolean(authState.token) && canViewCustomerAnalytics(authState.role),
    staleTime: 5 * 60 * 1000,
  });

  const detailsQuery = useQuery({
    queryKey: ["customer-checkout", selectedId, abandonMinutes],
    queryFn: () => fetchCustomerCheckout(selectedId, abandonMinutes),
    enabled: Boolean(selectedId),
  });

  const data = listQuery.data;
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const summary = data?.summary;

  const changeSort = (field) => {
    if (sort === field) setSortDir((dir) => (dir === "desc" ? "asc" : "desc"));
    else {
      setSort(field);
      setSortDir("desc");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    try {
      await exportCustomerCheckouts(filters);
    } catch (error) {
      setExportError(error.message || "تعذر تصدير البيانات");
    } finally {
      setExporting(false);
    }
  };

  const sortMark = (field) => (sort === field ? (sortDir === "desc" ? " ↓" : " ↑") : "");

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      <div className="mx-auto max-w-7xl px-3 py-3 md:px-4 md:py-6 space-y-3 md:space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" onClick={() => navigate("/dashboard")} className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 shrink-0">
              <ChevronLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-slate-900">تحليل العملاء</h1>
              <p className="hidden sm:block text-sm text-slate-500">مين دخل صفحة الدفع وكتب بياناته، ومين أكد الطلب فعلاً</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => listQuery.refetch()} className="rounded-lg md:rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 md:px-3 md:py-2 text-xs md:text-sm text-slate-700">
              <RefreshCw size={14} className="inline ml-1" />
              تحديث
            </button>
            <button type="button" onClick={exportCsv} disabled={exporting} className="rounded-lg md:rounded-xl bg-indigo-600 px-2.5 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white disabled:opacity-60">
              <Download size={14} className="inline ml-1" />
              {exporting ? "جاري التصدير..." : "تصدير CSV"}
            </button>
          </div>
        </div>

        {exportError && <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{exportError}</p>}

        <section className="grid grid-cols-3 gap-1.5 md:gap-3 xl:grid-cols-6">
          <SummaryCard label="دخلوا صفحة الدفع" value={summary?.totalCheckouts ?? "—"} />
          <SummaryCard label="كتب بياناته ولم يأكد" value={summary?.noOrderCheckouts ?? "—"} />
          <SummaryCard label="أكد والدفع ناقص" value={summary?.unpaidOrders ?? "—"} />
          <SummaryCard label="أكد الطلب" value={summary?.completedOrders ?? "—"} />
          <SummaryCard label="إجمالي المبالغ" value={summary ? formatMoney(summary.placedAmount) : "—"} />
          <SummaryCard label="استخدم كوبون" value={summary?.couponUsed ?? "—"} />
        </section>

        <section className="rounded-xl md:rounded-2xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className="md:hidden flex w-full items-center justify-between gap-2 px-3 py-2.5 text-sm font-semibold text-slate-800"
            aria-expanded={filtersOpen}
          >
            <span>الفلاتر</span>
            {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className={`${filtersOpen ? "grid" : "hidden"} md:grid gap-3 p-3 md:p-4 md:grid-cols-3 xl:grid-cols-4`}>
          <Field label="التاريخ">
            <select className={inputClass} value={preset} onChange={(e) => setPreset(e.target.value)}>
              {DATE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </Field>
          {preset === "custom" && (
            <>
              <Field label="من">
                <input className={inputClass} type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              </Field>
              <Field label="إلى">
                <input className={inputClass} type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </Field>
            </>
          )}
          <Field label="ماذا حصل">
            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </Field>
          <Field label="حالة الطلب">
            <select className={inputClass} value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}>
              {ORDER_STATUS_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </Field>
          <Field label="بيانات التواصل">
            <select className={inputClass} value={contact} onChange={(e) => setContact(e.target.value)}>
              {CONTACT_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </Field>
          <Field label="المبلغ من">
            <input className={inputClass} inputMode="decimal" value={minAmount} onChange={(e) => setMinAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" />
          </Field>
          <Field label="طريقة الدفع">
            <select className={inputClass} value={payment} onChange={(e) => setPayment(e.target.value)}>
              <option value="all">الكل</option>
              <option value="COD">الدفع عند الاستلام</option>
              <option value="Online">أونلاين / Kashier</option>
            </select>
          </Field>
          <Field label="الكوبون">
            <select className={inputClass} value={coupon} onChange={(e) => setCoupon(e.target.value)}>
              <option value="all">الكل</option>
              <option value="used">مستخدم</option>
              <option value="unused">غير مستخدم</option>
            </select>
          </Field>
          <Field label="كود الكوبون">
            <input className={inputClass} list="coupon-codes" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="SAVE20" />
            <datalist id="coupon-codes">
              {(codesQuery.data || []).map((code) => <option key={code} value={code} />)}
            </datalist>
          </Field>
          <Field label="بحث">
            <input className={inputClass} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="الاسم، الهاتف، البريد، رقم الطلب" />
          </Field>
          <Field label="الترتيب">
            <select
              className={inputClass}
              value={`${sort}:${sortDir}`}
              onChange={(e) => {
                const [field, dir] = e.target.value.split(":");
                setSort(field);
                setSortDir(dir);
              }}
            >
              <option value="checkoutDate:desc">وقت فتح الدفع · الأحدث</option>
              <option value="checkoutDate:asc">وقت فتح الدفع · الأقدم</option>
              <option value="orderDate:desc">تاريخ الطلب · الأحدث</option>
              <option value="amount:desc">المبلغ · الأعلى</option>
              <option value="amount:asc">المبلغ · الأقل</option>
            </select>
          </Field>
          <div className="md:col-span-3 xl:col-span-4 flex flex-wrap gap-4 text-sm text-slate-700">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={hasCart} onChange={(e) => setHasCart(e.target.checked)} /> حط منتج في السلة</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={hasWishlist} onChange={(e) => setHasWishlist(e.target.checked)} /> حط منتج في المفضلة</label>
          </div>
          <p className="md:col-span-3 xl:col-span-4 text-xs text-slate-500">الفلاتر بتتطبق على الجدول وعلى الشيت. الشيت مش بينزّل حد من غير موبايل أو إيميل.</p>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {listQuery.isLoading && <p className="p-6 text-slate-500">جاري التحميل...</p>}
          {listQuery.error && <p className="p-6 text-rose-700">{listQuery.error.message}</p>}
          {!listQuery.isLoading && !listQuery.error && (
            <>
            <div className="md:hidden divide-y divide-slate-100">
              {(data?.items || []).length === 0 && (
                <p className="px-3 py-8 text-center text-sm text-slate-500">لا توجد نتائج لهذه الفلاتر</p>
              )}
              {(data?.items || []).map((item) => (
                <button
                  key={item.sessionId}
                  type="button"
                  onClick={() => setSelectedId(item.sessionId)}
                  className="block w-full px-3 py-3 text-right"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900">{item.name || "بدون اسم"}</p>
                      <p className="text-sm text-slate-600" dir="ltr">{item.phone || "—"}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass(outcomeOf(item))}`}>
                      {OUTCOME_SHORT[outcomeOf(item)] || outcomeTextOf(item)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-700">{outcomeTextOf(item)}</p>
                  {item.email && <p className="mt-1 truncate text-xs text-slate-500">{item.email}</p>}
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
                    <span>الحالة: {orderStatusLabel(item)}</span>
                    <span>المبلغ: {formatMoney(item.orderAmount)}</span>
                    <span>{paymentLabel(item.paymentMethod)}</span>
                    <span>{item.couponUsed ? item.couponCode || "كوبون" : "بدون كوبون"}</span>
                    <span>{formatDateTime(item.checkoutStartedAt)}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-3 text-right font-medium">العميل</th>
                    <th className="px-3 py-3 text-right font-medium">الموبايل</th>
                    <th className="px-3 py-3 text-right font-medium">الإيميل</th>
                    <th className="px-3 py-3 text-right font-medium">ماذا حصل</th>
                    <th className="px-3 py-3 text-right font-medium">حالة الطلب</th>
                    <th className="px-3 py-3 text-right font-medium"><button type="button" onClick={() => changeSort("amount")}>المبلغ{sortMark("amount")}</button></th>
                    <th className="px-3 py-3 text-right font-medium">طريقة الدفع</th>
                    <th className="px-3 py-3 text-right font-medium">الكوبون</th>
                    <th className="px-3 py-3 text-right font-medium"><button type="button" onClick={() => changeSort("checkoutDate")}>وقت فتح الدفع{sortMark("checkoutDate")}</button></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-500">لا توجد نتائج لهذه الفلاتر</td></tr>
                  )}
                  {(data?.items || []).map((item) => (
                    <tr key={item.sessionId} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => setSelectedId(item.sessionId)}>
                      <td className="px-3 py-3 font-medium text-slate-900">{item.name || "—"}</td>
                      <td className="px-3 py-3" dir="ltr">{item.phone || "—"}</td>
                      <td className="px-3 py-3">{item.email || "—"}</td>
                      <td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(outcomeOf(item))}`}>{outcomeTextOf(item)}</span></td>
                      <td className="px-3 py-3">{orderStatusLabel(item)}</td>
                      <td className="px-3 py-3">{formatMoney(item.orderAmount)}</td>
                      <td className="px-3 py-3">{paymentLabel(item.paymentMethod)}</td>
                      <td className="px-3 py-3">{item.couponUsed ? item.couponCode || "نعم" : "لا"}</td>
                      <td className="px-3 py-3">{formatDateTime(item.checkoutStartedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-3 py-3 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <span>عدد الصفوف</span>
              <select className="rounded-lg border border-slate-200 px-2 py-1" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              <span>{total} نتيجة</span>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40">السابق</button>
              <span>{page} / {pageCount}</span>
              <button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40">التالي</button>
            </div>
          </div>
        </section>
      </div>

      {selectedId && (
        <div className="fixed inset-0 z-40 flex justify-start bg-slate-900/40" onClick={() => setSelectedId(null)}>
          <aside className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} dir="rtl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><UserRound size={18} /> تفاصيل العميل</h2>
              <button type="button" onClick={() => setSelectedId(null)} className="text-sm text-slate-500">إغلاق</button>
            </div>
            {detailsQuery.isLoading && <p className="text-slate-500">جاري التحميل...</p>}
            {detailsQuery.error && <p className="text-rose-700">{detailsQuery.error.message}</p>}
            {detailsQuery.data && <Details details={detailsQuery.data} />}
          </aside>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-xl md:rounded-2xl border border-slate-200 bg-white px-2 py-1.5 md:px-4 md:py-3 min-w-0">
      <p className="text-[10px] md:text-xs text-slate-500 leading-tight line-clamp-2">{label}</p>
      <p className="mt-0.5 md:mt-1 text-sm md:text-2xl font-bold text-slate-900 leading-tight break-words">{value}</p>
    </div>
  );
}

function Details({ details }) {
  const customer = [
    ["الاسم", details.name],
    ["الهاتف", details.phone],
    ["البريد", details.email],
    ["العنوان", details.address],
    ["المحافظة", details.governorate],
    ["المدينة", details.city],
  ].filter(([, value]) => value);

  return (
    <div className="space-y-5 text-sm text-slate-800">
      <section>
        <h3 className="mb-2 font-bold">العميل</h3>
        {customer.length === 0 && <p className="text-slate-500">لم تُدخل بيانات بعد</p>}
        {customer.map(([label, value]) => (
          <p key={label} className="flex justify-between gap-4 border-b border-slate-100 py-2"><span className="text-slate-500">{label}</span><span dir="auto">{value}</span></p>
        ))}
      </section>
      <section>
        <h3 className="mb-2 font-bold">حركته في الموقع</h3>
        <p className="flex justify-between py-1"><span>صفحات فتحها</span><span>{details.pageViews}</span></p>
        <p className="flex justify-between py-1"><span>حط في المفضلة</span><span>{details.wishlistAdds}</span></p>
        <p className="flex justify-between py-1"><span>حط في السلة</span><span>{details.cartAdds}</span></p>
        <p className="flex justify-between py-1"><span>فتح السلة</span><span>{details.cartViews}</span></p>
        <p className="flex justify-between py-1"><span>فتح صفحة الدفع</span><span>{details.checkoutViews}</span></p>
        <p className="flex justify-between py-1"><span>عدد القطع في السلة</span><span>{details.cartItemCount}</span></p>
      </section>
      <section>
        <h3 className="mb-2 font-bold">صفحة الدفع</h3>
        <p className="flex justify-between py-1"><span>فتحها</span><span>{formatDateTime(details.checkoutStartedAt)}</span></p>
        <p className="flex justify-between py-1"><span>آخر حاجة عملها</span><span>{formatDateTime(details.lastActivityAt)}</span></p>
        <p className="flex justify-between py-1"><span>طريقة الدفع</span><span>{paymentLabel(details.paymentMethod)}</span></p>
        <p className="flex justify-between py-1"><span>الكوبون</span><span>{details.couponUsed ? details.couponCode || "نعم" : "لا"}</span></p>
      </section>
      <section>
        <h3 className="mb-2 font-bold">الطلب</h3>
        <p className={`rounded-xl px-3 py-2 font-medium ${statusClass(outcomeOf(details))}`}>{outcomeTextOf(details)}</p>
        <p className="flex justify-between py-1"><span>أكد الطلب؟</span><span>{details.placedOrder || details.orderId || details.pendingOrderId ? "نعم" : "لا"}</span></p>
        <p className="flex justify-between py-1"><span>الدفع خلص؟</span><span>{paymentFinishedLabel(details)}</span></p>
        <p className="flex justify-between py-1"><span>رقم الطلب</span><span>{details.orderId ? `#${details.orderId}` : details.pendingOrderId ? `#${details.pendingOrderId}` : "—"}</span></p>
        <p className="flex justify-between py-1"><span>حالة الطلب</span><span>{orderStatusLabel(details)}</span></p>
        <p className="flex justify-between py-1"><span>مبلغ الشراء</span><span>{formatMoney(details.orderAmount)}</span></p>
        {(details.orderId || details.pendingOrderId) && (
          <p className="flex justify-between py-1"><span>تاريخ الطلب</span><span>{formatDateTime(details.orderCreatedAt)}</span></p>
        )}
      </section>
    </div>
  );
}
